import React, { useEffect, useState } from "react";
import { Mail, X, ArrowRight, Loader2 } from "lucide-react";
import { dataTrigger } from "@/analytics";
import { TG_OFFER_PDF_URL } from "@/constants/offers";
import { Button } from "@/ui/Button/Button";
import cls from "./styles.module.scss";

interface EmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string) => void | Promise<void>;
}

export function EmailModal({ isOpen, onClose, onSubmit }: EmailModalProps) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      return;
    }
    const t = setTimeout(() => setShouldRender(false), 250);
    return () => clearTimeout(t);
  }, [isOpen]);

  const validateEmail = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    if (!email) {
      setError("Пожалуйста, введите email");
      return;
    }
    if (!validateEmail(email)) {
      setError("Пожалуйста, введите корректный email");
      return;
    }

    setError("");

    try {
      setIsLoading(true);
      await onSubmit(email);
      setEmail("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    setEmail("");
    setError("");
    onClose();
  };

  if (!shouldRender) return null;

  return (
    <>
      <div
        className={`${cls.backdrop} ${isOpen ? cls.open : cls.close}`}
        onClick={handleClose}
      />

      <div className={cls.wrapper}>
        <div className={`${cls.modal} ${isOpen ? cls.open : cls.close}`}>
          <button
            onClick={handleClose}
            className={cls.closeBtn}
            type="button"
            disabled={isLoading}
            aria-disabled={isLoading}
            {...dataTrigger("bkn.email_modal.close")}
          >
            <X className={cls.closeIcon} />
          </button>

          <div className={cls.iconRow}>
            <div className={cls.iconBubble}>
              <Mail className={cls.iconMain} />
            </div>
          </div>

          <h2 className={cls.title}>Введите ваш email</h2>
          <p className={cls.description}>
            Чек об оплате будет отправлен на указанный адрес
          </p>

          <form onSubmit={handleSubmit} className={cls.form}>
            <div>
              <div className={cls.inputWrap}>
                <Mail className={cls.inputIcon} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError("");
                  }}
                  placeholder="example@mail.com"
                  className={cls.input}
                  disabled={isLoading}
                />
              </div>

              {error ? <p className={cls.error}>{error}</p> : null}
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={isLoading}
              aria-busy={isLoading}
              {...dataTrigger("bkn.email_modal.submit")}
              leftIcon={
                isLoading ? <Loader2 className={cls.loaderIcon} /> : undefined
              }
              rightIcon={
                !isLoading ? <ArrowRight className={cls.arrow} /> : undefined
              }
            >
              {isLoading ? "Подготовка оплаты..." : "Продолжить к оплате"}
            </Button>

            <p className={cls.info}>Мы не передаем ваши данные третьим лицам</p>
            <p className={cls.offerText}>
              Нажимая кнопку &quot;Продолжить к оплате&quot;
              <br />
              вы соглашаетесь с правилами{" "}
              <a
                href={TG_OFFER_PDF_URL}
                target="_blank"
                rel="noreferrer"
                className={cls.offerLink}
              >
                оферты
              </a>
            </p>
          </form>
        </div>
      </div>
    </>
  );
}
